/**
 * Feedback (`/feedback`): everyone, under Support.
 *
 * The house list layout: one white card with a tab bar, the toolbar (search,
 * status and type filters, New request), a plain-header sortable table and
 * the pagination. Everyone has **My requests**; admins also get **All
 * requests**, where they triage everyone's, and a banner that counts the new
 * ones and filters to them. Sorting, filtering and paging run on the server
 * and live in the URL, so a request's page returns to the same view.
 */

import { Inbox, MessagesSquare, Plus, Search, UserRound } from 'lucide-react';

import type { FeedbackCategory, FeedbackStatus } from '@/api/types';
import { MultiSelect } from '@/ui/MultiSelect';
import { PageHead } from '@/ui/PageHead';
import { LoadFailure, LoadingCard } from '@/ui/StateCard';

import { FeedbackDialog } from './FeedbackDialog';
import { FeedbackTable } from './FeedbackTable';
import { CATEGORIES, STATUSES } from './feedbackUi';
import { type FeedbackPageState, useFeedbackPage } from './useFeedbackPage';

const SUBTITLE = {
  all: 'Everyone’s requests: reply on each and set its status as you work through them',
  mine: 'Report a bug, propose an enhancement or a suggestion, or ask a question, and follow what happens to it',
} as const;

const SEARCH_LABEL = { all: 'Search by title or person', mine: 'Search by title' } as const;

export function FeedbackPage() {
  const state = useFeedbackPage();
  const { list } = state;

  return (
    <>
      {/* Follows the tab: My requests reads the same for everyone; All requests is triage. */}
      <PageHead title="Feedback" subtitle={SUBTITLE[state.params.scope]} />

      <div className="card list-card">
        <FeedbackTabs state={state} />
        <FeedbackToolbar state={state} />
        <NewRequestsBanner state={state} />

        {list.isPending && <LoadingCard label="Loading requests" />}
        {!list.isPending && list.error && <LoadFailure error={list.error} onRetry={() => void list.refetch()} />}
        {!list.isPending && !list.error && <FeedbackTable state={state} />}
      </div>

      {state.adding && <FeedbackDialog onClose={() => state.setAdding(false)} />}
    </>
  );
}

function FeedbackTabs({ state }: { state: FeedbackPageState }) {
  const { all, newCount } = state;
  return (
    <div className="tabs card-tabs" role="tablist" aria-label="Requests">
      <button
        type="button"
        role="tab"
        aria-selected={!all}
        className={`tab${all ? '' : ' is-active'}`}
        onClick={() => state.switchTab('mine')}
      >
        <UserRound aria-hidden="true" />
        My requests
      </button>
      {state.canTriage && (
        <button
          type="button"
          role="tab"
          aria-selected={all}
          className={`tab${all ? ' is-active' : ''}`}
          onClick={() => state.switchTab('all')}
        >
          <MessagesSquare aria-hidden="true" />
          All requests
          {newCount > 0 && <span className="count" aria-label={`${newCount} new`}>{newCount}</span>}
        </button>
      )}
    </div>
  );
}

function FeedbackToolbar({ state }: { state: FeedbackPageState }) {
  const { params, update } = state;
  const counts = state.list.data?.counts;
  const searchLabel = SEARCH_LABEL[params.scope];
  return (
    <div className="toolbar filter-panel">
      <div className="search-field">
        <Search aria-hidden="true" />
        <input
          type="search"
          value={state.search}
          placeholder={searchLabel}
          aria-label={searchLabel}
          onChange={(e) => state.setSearch(e.target.value)}
        />
      </div>
      <MultiSelect
        label="Filter by status"
        allLabel="All statuses"
        noun="statuses"
        options={STATUSES.map((s) => ({ value: s.value, label: s.label, meta: counts?.[s.value] ?? 0 }))}
        value={params.statuses}
        onChange={(statuses) => update({ statuses: statuses as FeedbackStatus[] })}
      />
      <MultiSelect
        label="Filter by type"
        allLabel="All types"
        noun="types"
        options={CATEGORIES.map((c) => ({ value: c.value, label: c.label }))}
        value={params.categories}
        onChange={(categories) => update({ categories: categories as FeedbackCategory[] })}
      />
      {state.canSubmit && (
        <button type="button" className="btn btn-primary" onClick={() => state.setAdding(true)}>
          <Plus aria-hidden="true" /> New request
        </button>
      )}
    </div>
  );
}

function NewRequestsBanner({ state }: { state: FeedbackPageState }) {
  const { newCount, params } = state;
  if (!state.all || newCount <= 0) return null;
  const showingNew = params.statuses.length === 1 && params.statuses[0] === 'new';
  const one = newCount === 1;
  return (
    <div className="alert alert-info form-alert" role="status">
      <Inbox aria-hidden="true" />
      <div className="a-body">
        <b>
          {newCount} new {one ? 'request' : 'requests'}
        </b>{' '}
        {one ? 'is' : 'are'} waiting for a first look. Open one to reply and set its status.
      </div>
      <button type="button" className="btn btn-secondary btn-sm" onClick={() => state.update({ statuses: showingNew ? [] : ['new'] })}>
        {showingNew ? 'Show all' : 'Show new'}
      </button>
    </div>
  );
}
